import React, { useState } from 'react';
import { styled } from '@linaria/react';
import { COLORS, Icon, Modal, Tooltip as OriginalTooltip } from '@livecommerce/design-system';
import { css } from '@linaria/core';
import PropTypes from 'prop-types';
import { m } from 'framer-motion';
import { useSelector } from 'react-redux';

import NavigationList from 'components-v3/NavigationList/index.jsx';
import ProfileMenu from 'components-v3/ProfileMenu/index.jsx';
import ProductsMenu from 'components-v3/ProductsMenu/index.jsx';
import ProfileImage from 'components-v3/ProfileImage/index.jsx';
import NavigationDropdown from './components-v3/NavigationDropdown/index.jsx';
import DevIndicator from 'components-v3/DevIndicator/DevIndicator.jsx';

import { mainTransitionConfig } from './constants/animationConfig.js';
import { isProduction, projectIdWithEnv } from 'constants/gcp.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useModal } from 'hooks/index.js';

const logoStyles = css`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  margin-left: 12px;
  flex-shrink: 0;
  pointer-events: none;
`;

const InteractiveElementButton = styled.button`
  position: relative;
  padding: 0;
  border: none;
  border-radius: 6px;
  width: 44px;
  height: 44px;
  background-color: ${(props) => (props.active ? props.activeBackgroundColor : props.backgroundColor)};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.activeBackgroundColor};
  }
`;

const InteractiveElementContainer = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  transform: translateY(${(props) => (props.collapsed ? '-8px' : '44px')});
  transition: transform 0.25s cubic-bezier(0.7, -0.4, 0.4, 1.4);
  z-index: 2;
  align-self: flex-start;

  > * {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Tooltip = styled(OriginalTooltip)`
  align-self: flex-end;
`;

const AnimatedCollapseIcon = styled(Icon)`
  transform: ${(props) => (props.collapsed ? 'rotate(-90deg)' : 'rotate(-270deg)')};
  transition: transform 0.25s cubic-bezier(0.7, -0.4, 0.4, 1.4);
`;

const AnimatedNav = (props) => <m.nav {...props} />;
const Container = styled(AnimatedNav)`
  padding-top: 12px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  z-index: 1;
`;

const Sidebar = ({ collapsed, setIsSidebarCollapsed, style, className, ...props }) => {
  const authUser = useSelector((state) => state.sessionState.authUser);
  const [isOrganizationSwitcherOpened, setIsOrganizationSwitcherOpened] = useState(false);
  const [isProductsMenuOpened, setIsProductsMenuOpened] = useState(false);
  const [isProfileMenuOpened, setIsProfileMenuOpened] = useState(false);
  const { isOpen: isDevIndicatorModalOpen, open: openDevIndicatorModal, close: closeDevIndicatorModal } = useModal();

  const onCollapseButtonClick = () => {
    const nextSidebarCollapsedState = !collapsed;
    trackingService.track({
      eventType: `sidebar-toggle-collapse:${nextSidebarCollapsedState ? 'collapse' : 'expand'}`,
    });
    setIsSidebarCollapsed(nextSidebarCollapsedState);
  };

  if (!authUser) return null;

  const handleProductsMenuClick = () => {
    setIsProductsMenuOpened((prevValue) => {
      const nextOpenedState = !prevValue;
      trackingService.track({
        eventType: `sidebar-products-menu:${nextOpenedState ? 'open' : 'close'}`,
      });
      return nextOpenedState;
    });
  };
  const handleOrganizationDropdownClickOutside = () => {
    setIsProductsMenuOpened(false);
    setIsOrganizationSwitcherOpened(false);
  };
  const handleProfileMenuClick = () => {
    setIsProfileMenuOpened((prevValue) => {
      const nextOpenedState = !prevValue;
      trackingService.track({
        eventType: `sidebar-profile-menu:${nextOpenedState ? 'open' : 'close'}`,
      });
      return nextOpenedState;
    });
  };
  const handleProfileDropdownClickOutside = () => setIsProfileMenuOpened(false);

  const handleDevIndicatorClick = () => openDevIndicatorModal();
  const handleDevIndicatorModalClose = () => closeDevIndicatorModal();

  return (
    <Container
      style={style}
      transition={mainTransitionConfig}
      initial={false}
      animate={{
        paddingLeft: collapsed ? '10px' : '20px',
        paddingRight: collapsed ? '10px' : '20px',
      }}
      className={className}
      {...props}
    >
      <div className={logoStyles}>
        {!collapsed && (
          <Icon
            name="logoHorizontal"
            color={isProduction ? COLORS.dark_700 : COLORS.purple_500}
            data-test-id="sidebarBambuserLogo"
          />
        )}
      </div>
      <NavigationList setIsSidebarCollapsed={setIsSidebarCollapsed} collapsed={collapsed} />
      <InteractiveElementContainer collapsed={collapsed}>
        {!isProduction && (
          <>
            <Tooltip tooltipPosition="right" tooltipText={projectIdWithEnv()} id="expandTooltip">
              <InteractiveElementButton
                type="button"
                active={isDevIndicatorModalOpen}
                backgroundColor={COLORS.orangeMedium}
                activeBackgroundColor={COLORS.orangeLight}
                aria-label="openDevIndicator"
                onClick={handleDevIndicatorClick}
              >
                <Icon name="apple" />
              </InteractiveElementButton>
            </Tooltip>
            <Modal
              name="DevIndicatorModal"
              isModalOpen={isDevIndicatorModalOpen}
              onClose={handleDevIndicatorModalClose}
              title="iOS Testing"
            >
              <DevIndicator />
            </Modal>
          </>
        )}
        <Tooltip tooltipText="Apps" id="appsTooltip" isShown={collapsed}>
          <NavigationDropdown
            isOpened={isProductsMenuOpened}
            onClickOutside={handleOrganizationDropdownClickOutside}
            isControlled
            dropdownContent={
              <ProductsMenu
                isOrganizationSwitcherOpened={isOrganizationSwitcherOpened}
                setIsOrganizationSwitcherOpened={setIsOrganizationSwitcherOpened}
                setIsProductsMenuOpened={setIsProductsMenuOpened}
              />
            }
          >
            <InteractiveElementButton
              type="button"
              active={isProductsMenuOpened}
              backgroundColor="transparent"
              activeBackgroundColor={COLORS.white}
              aria-label="toggleProductsMenu"
              onClick={handleProductsMenuClick}
            >
              <Icon name="apps" />
            </InteractiveElementButton>
          </NavigationDropdown>
        </Tooltip>
        <Tooltip tooltipText="Profile" id="profileTooltip" isShown={collapsed}>
          <NavigationDropdown
            isOpened={isProfileMenuOpened}
            onClickOutside={handleProfileDropdownClickOutside}
            isControlled
            dropdownContent={<ProfileMenu setIsOpened={setIsProfileMenuOpened} />}
          >
            <InteractiveElementButton
              type="button"
              active={isProfileMenuOpened}
              backgroundColor="transparent"
              activeBackgroundColor={COLORS.white}
              aria-label="toggleProfileMenu"
              onClick={handleProfileMenuClick}
              data-test-id="profileMenuDropdown"
            >
              <ProfileImage alt="User Avatar icon" />
            </InteractiveElementButton>
          </NavigationDropdown>
        </Tooltip>
      </InteractiveElementContainer>
      <Tooltip tooltipText="Expand" id="expandTooltip" isShown={collapsed}>
        <InteractiveElementButton
          type="button"
          backgroundColor="transparent"
          activeBackgroundColor={COLORS.white}
          aria-label="toggleCollapse"
          onClick={onCollapseButtonClick}
        >
          <AnimatedCollapseIcon name="download" collapsed={collapsed} />
        </InteractiveElementButton>
      </Tooltip>
    </Container>
  );
};

Sidebar.propTypes = {
  collapsed: PropTypes.bool,
  setIsSidebarCollapsed: PropTypes.func,
};

export default Sidebar;
